// eslint-disable-next-line max-classes-per-file
import { IAttribute } from './annotation';
import { Metamodel } from './model';
import { Named } from './named';
import { isMap, isSet } from 'lodash';

export enum Direction {
    GO,  // Continues walking
    SKIP, // Skips this step. This can be either referrable or not properties
    STOP  // Stops walking all together
}

export interface IPropertyInspector {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPropertyNames(object: any): string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPropertyValue(object: any, property: string): any;
}

interface IDependencyNode {
    named: Named;
    dependencies?: IDependencyNode[];
    dependentCount?: number;
}

export class DefaultPropertyInspector implements IPropertyInspector {

    public static readonly instance = new DefaultPropertyInspector();

    private constructor() { }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getPropertyNames(object: any): string[] {
        return Object.getOwnPropertyNames(object);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getPropertyValue(object: any, property: string): any {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (object as any)[property];
    }
}

export class Walker {

    // eslint-disable-next-line brace-style
    private constructor() { }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static walk(context: { inlineDataset?: boolean }, step: ((object: any, property: string, value: any, attribute?: IAttribute, ...parameters: any[]) => Direction),
        propertyInspector: IPropertyInspector,
        ...objects: Named[]): void {
        // We walk all model objects and their attributes. Any reference to a concept is walked only once
        const visitedConceptObjects = new Set<Named>();
        objects.forEach((object) => visitedConceptObjects.add(object));
        for (const object of objects) {
            try {
                if (object == null) {
                    continue;
                }
                const model = Metamodel.modelOf(object);
                for (const property of propertyInspector.getPropertyNames(object)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const value = propertyInspector.getPropertyValue(object, property);
                    const attribute = model.hasAttribute(property) ? model.attribute(property) : undefined;
                    // do not continue if there's an attribute and we can get a template value out of it
                    if (!value && !(!!attribute && attribute.templatable && !!attribute.templateValue(object))) { // Should work for any falsy value including nulls
                        continue;
                    }

                    if (attribute && attribute.transient) {
                        continue;
                    }

                    // make sure we can continue with null attribute, since there may be named reference inside its value

                    // Either this is a collection or a single value.
                    if (Array.isArray(value)) {
                        for (let index = 0; index < value.length; index++) {
                            // Ignore undefined value in Array if exists
                            if (value[index] !== undefined) {
                                const currentValue = value[index];
                                const direction = Walker.walkValue(context, step, object, property, value[index], visitedConceptObjects, attribute, index);
                                if (direction === Direction.STOP) {
                                    return;
                                } else if (direction === Direction.GO) {
                                    objects.push(currentValue); // considered contained
                                }
                            }
                        }
                    } else if (isSet(value)) {
                        for (const item of value) {
                            const direction = Walker.walkValue(context, step, object, property, item, visitedConceptObjects, attribute);
                            if (direction === Direction.STOP) {
                                return;
                            } else if (direction === Direction.GO) {
                                objects.push(item); // considered contained
                            }
                        }
                    } else if (isMap(value)) {
                        for (const [key, item] of value) {
                            // Two callbacks only for value then key. Reason for this is for anyone substituing the key is valid when value shows up
                            let direction = Walker.walkValue(context, step, object, property, item, visitedConceptObjects, attribute, key);
                            if (direction === Direction.STOP) {
                                return;
                            } else if (direction === Direction.GO) {
                                objects.push(item); // considered contained
                            }

                            direction = Walker.walkValue(context, step, object, property, key, visitedConceptObjects, attribute, key);
                            if (direction === Direction.STOP) {
                                return;
                            } else if (direction === Direction.GO) {
                                objects.push(key); // considered contained
                            }
                        }
                    } else {
                        const direction = Walker.walkValue(context, step, object, property, value, visitedConceptObjects, attribute);
                        if (direction === Direction.STOP) {
                            return;
                        } else if (direction === Direction.GO) {
                            objects.push(value); // considered contained
                        }
                    }
                }
            } catch (e) {
                let resourceName = object.name;
                if (object.conceptOwner && object.conceptOwner.named) {
                    resourceName = `${object.conceptOwner.named.name}:${object.name}`;
                }
                e.message = `Failed on resource ${resourceName}. Reason: ${e.message}`;
                throw e;
            }
        }
    }

    /*
        Returns all the direct and indirect dependencies of this object
    */
    public static conceptDependencies(context: { inlineDataset?: boolean }, propertyInspector?: IPropertyInspector, ...nameds: Named[]): Set<Named> {
        return Walker.conceptDependenciesWithConceptFilter(context, propertyInspector, undefined, ...nameds);
    }

    /*
        Returns all the direct and indirect dependencies of this object with filter, all the depedencies matches the conceptFilter will be excluded
    */
    public static conceptDependenciesWithConceptFilter(context: { inlineDataset?: boolean }, propertyInspector?: IPropertyInspector, conceptFilter?: string[], ...nameds: Named[]): Set<Named> {
        const namedSet = new Set<Named>();

        Walker.walk(context, (_object, _property, value, attribute) => {
            if (!value) {
                return Direction.SKIP;
            }
            if ((attribute && attribute.primitive) || !Metamodel.isModelOf(value)) {
                return Direction.GO;
            }
            const valueModel = Metamodel.modelOf(value);
            if (valueModel.concept && Named.MODEL.isInstance(value) && !((value as Named).owner)) {
                if (conceptFilter && conceptFilter.indexOf(valueModel.name) > -1) {
                    return Direction.SKIP;
                }
                namedSet.add(value);
            }
            return Direction.GO;
        },
            propertyInspector ? propertyInspector : DefaultPropertyInspector.instance,
            ...nameds);

        return namedSet;
    }

    /*
        Returned a concept dependencies ordered in a array so that the most dependent named appears first and least appears last
        self flag asks if passed named should be ordered and included as well
    */
    public static orderedConceptDependencies(context: { inlineDataset?: boolean }, propertyInspector?: IPropertyInspector, self?: boolean, ...nameds: Named[]): Named[] {
        const namedToDependencyMap = new Map<Named, IDependencyNode>();
        const namedQueue: Named[] = [];
        for (const anamed of nameds) {
            namedToDependencyMap.set(anamed, { named: anamed });
            namedQueue.push(anamed);
        }

        let named: Named | undefined;
        let dependencyNode: IDependencyNode | undefined;
        // eslint-disable-next-line no-cond-assign
        while ((named = namedQueue.pop())) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const node = namedToDependencyMap.get(named)!;
            const dependencies = Walker.directConceptDependencies(context, named, propertyInspector);
            for (const dependency of dependencies) {
                dependencyNode = namedToDependencyMap.get(dependency);
                if (!dependencyNode) {
                    dependencyNode = { named: dependency };
                    namedToDependencyMap.set(dependency, dependencyNode);
                    namedQueue.push(dependency);
                }
                node.dependencies = node.dependencies || [];
                // This is a one time use graph with this counting down
                dependencyNode.dependentCount = (dependencyNode.dependentCount || 0) + 1;
                node.dependencies.push(dependencyNode);
            }
        }

        const orderedNamedQueue: Named[] = [];
        // Now we have all dependencies in the map. We will start only at the nodes in the nameds that do not
        // have inbounds
        const dependencyQueue: IDependencyNode[] = [];
        for (const anamed of nameds) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const node = namedToDependencyMap.get(anamed)!;
            if (!node.dependentCount) { // Falsy value
                dependencyQueue.push(node);
                if (self) {
                    orderedNamedQueue.push(node.named);
                }
            }
        }

        // If any of my parents are left unvisted we skip it until all of them are visited
        // eslint-disable-next-line no-cond-assign
        while (dependencyNode = dependencyQueue.pop()) {
            if (!dependencyNode.dependencies) {
                continue;
            }

            for (const childDependencyNode of dependencyNode.dependencies) {
                if (!childDependencyNode.dependentCount) {
                    continue; // Can't really happen unless circular dependency
                } else {
                    childDependencyNode.dependentCount--;
                }
                // If all dependents scanned its time to snatch and bag him
                if (childDependencyNode.dependentCount === 0) {
                    orderedNamedQueue.push(childDependencyNode.named);
                    dependencyQueue.push(childDependencyNode);
                }
            }
        }

        return orderedNamedQueue;
    }

    /*
        Returns only the direct concept dependencies of this object
    */
    public static directConceptDependencies(context: { inlineDataset?: boolean }, named: Named, propertyInspector?: IPropertyInspector): Set<Named> {
        const namedSet = new Set<Named>();

        Walker.walk(context, (_object, _property, value, attribute) => {
            if (!value) {
                return Direction.SKIP;
            }
            if ((attribute?.primitive) || !Metamodel.isModelOf(value)) {
                return Direction.GO;
            }
            const valueModel = Metamodel.modelOf(value);
            if (attribute?.isReferenced(context)) {
                if (valueModel.concept) {
                    namedSet.add(value);
                    return Direction.SKIP; // Skip going any further
                } else if (valueModel.globalScope) {
                    return Direction.SKIP; // Skip any references to global scope variables
                }
            }
            return Direction.GO;
        },
            propertyInspector ? propertyInspector : DefaultPropertyInspector.instance,
            named);

        return namedSet;
    }

    public static hasDirectConceptDependencies(context: { inlineDataset?: boolean }, named: Named, target: Named): boolean {
        let found = false;

        Walker.walk(context, (_object, _property, value, attribute) => {
            if (found) {
                return Direction.STOP;
            }
            if (!value) {
                return Direction.SKIP;
            }
            if ((attribute?.primitive) || !Metamodel.isModelOf(value)) {
                return Direction.GO;
            }
            const valueModel = Metamodel.modelOf(value);
            if (attribute?.isReferenced(context)) {
                if (valueModel.concept) {
                    found = value === target;
                    return found ? Direction.STOP : Direction.SKIP; // Skip going any further
                } else if (valueModel.globalScope) {
                    return Direction.SKIP; // Skip any references to global scope variables
                }
            }
            return Direction.GO;
        },
            DefaultPropertyInspector.instance,
            named);

        return found;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static walkValue(context: { inlineDataset?: boolean }, step: ((object: any, property: string, value: any, attribute?: IAttribute, ...parameters: any[]) => Direction),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object: {}, property: string, value: any, visitedConceptObjects: Set<Named>, attribute?: IAttribute, ...parameters: any[]): Direction {
        const direction = step(object, property, value, attribute, parameters);
        if (direction === Direction.STOP) {
            return Direction.STOP;
        }

        if ((attribute?.primitive) || !Metamodel.isModelOf(value)) {
            return Direction.SKIP;
        }
        const attributeModel = Metamodel.modelOf(value);

        if (attribute?.isReferenced(context)) {
            if (!attributeModel.concept) { // For now we don't deal with references that transcend concept objects
                return Direction.SKIP;
            }
            // Else add it to the list that we are traversing
            if (visitedConceptObjects.has(value as Named)) {
                return Direction.SKIP;
            }
            visitedConceptObjects.add(value as Named);
        }
        return direction; // This could be a skip returned by the step. We still need to visit them
    }
}
